/* Canonical option constants for the offer-skills flow (talent_profiles, user-facing "Volunteer").
 *
 * profession = single-select (~70 options, grouped); contribution_types = multi-select (~22). Each
 * option is { value (stable snake_case id), label (display English), group }. Values are stable IDs
 * stored in the DB; labels are display text.
 *
 * i18n: labels are English only for now. Translating these ~90 strings to hi/te is a follow-up —
 * English is the fallback; NO machine translation.
 *
 * professionToCategory (bottom) auto-derives the existing NOT NULL `category` column from the new
 * `profession` field, mapping into the LIVE category enum (medical|legal|trades|education|finance|
 * it|other). The originally-specced target values (imam_scholar/teacher/it_digital/general) do NOT
 * exist in that enum, so the mapping targets the live values instead — see the note on the function. */

export type ProfessionGroup =
  | 'Healthcare'
  | 'Education & Islamic'
  | 'Professional'
  | 'Skilled Trades'
  | 'Business & Commerce'
  | 'Services'
  | 'Agriculture'
  | 'Public & Community'
  | 'Other'

export interface ProfessionOption {
  value: string
  label: string
  group: ProfessionGroup
}

export interface ContributionOption {
  value: string
  label: string
}

/* Single-select profession list — VERBATIM from the approved list, grouped as given. */
export const PROFESSION_OPTIONS: ProfessionOption[] = [
  // Healthcare
  { value: 'doctor', label: 'Doctor', group: 'Healthcare' },
  { value: 'dentist', label: 'Dentist', group: 'Healthcare' },
  { value: 'nurse', label: 'Nurse', group: 'Healthcare' },
  { value: 'pharmacist', label: 'Pharmacist', group: 'Healthcare' },
  { value: 'physiotherapist', label: 'Physiotherapist', group: 'Healthcare' },
  { value: 'hakeem_unani', label: 'Hakeem / Unani Practitioner', group: 'Healthcare' },
  { value: 'other_healthcare', label: 'Other Healthcare Professional', group: 'Healthcare' },

  // Education & Islamic
  { value: 'teacher', label: 'Teacher', group: 'Education & Islamic' },
  { value: 'professor_lecturer', label: 'Professor / Lecturer', group: 'Education & Islamic' },
  { value: 'student', label: 'Student', group: 'Education & Islamic' },
  { value: 'islamic_scholar_aalim', label: 'Islamic Scholar / Aalim', group: 'Education & Islamic' },
  { value: 'aalimah', label: 'Aalimah', group: 'Education & Islamic' },
  { value: 'hafiz_hafiza', label: 'Hafiz / Hafiza', group: 'Education & Islamic' },
  { value: 'qari_qariah', label: 'Qari / Qariah', group: 'Education & Islamic' },
  { value: 'quran_arabic_teacher', label: 'Quran / Arabic Teacher', group: 'Education & Islamic' },
  { value: 'madrasa_teacher', label: 'Madrasa Teacher', group: 'Education & Islamic' },

  // Professional
  { value: 'engineer', label: 'Engineer', group: 'Professional' },
  { value: 'it_software', label: 'IT / Software Professional', group: 'Professional' },
  { value: 'chartered_accountant', label: 'Chartered Accountant', group: 'Professional' },
  { value: 'accountant_finance', label: 'Accountant / Finance Professional', group: 'Professional' },
  { value: 'lawyer', label: 'Lawyer', group: 'Professional' },
  { value: 'architect', label: 'Architect', group: 'Professional' },
  { value: 'hr_professional', label: 'HR Professional', group: 'Professional' },
  { value: 'management_consultant', label: 'Management / Consultant', group: 'Professional' },
  { value: 'marketing_professional', label: 'Marketing Professional', group: 'Professional' },
  { value: 'designer_creative', label: 'Designer / Creative Professional', group: 'Professional' },
  { value: 'other_professional', label: 'Other Professional', group: 'Professional' },

  // Skilled Trades
  { value: 'electrician', label: 'Electrician', group: 'Skilled Trades' },
  { value: 'plumber', label: 'Plumber', group: 'Skilled Trades' },
  { value: 'carpenter', label: 'Carpenter', group: 'Skilled Trades' },
  { value: 'mechanic', label: 'Mechanic', group: 'Skilled Trades' },
  { value: 'ac_refrigeration', label: 'AC / Refrigeration Technician', group: 'Skilled Trades' },
  { value: 'painter', label: 'Painter', group: 'Skilled Trades' },
  { value: 'mason', label: 'Mason', group: 'Skilled Trades' },
  { value: 'welder_fabricator', label: 'Welder / Fabricator', group: 'Skilled Trades' },
  { value: 'tailor', label: 'Tailor', group: 'Skilled Trades' },
  { value: 'barber', label: 'Barber', group: 'Skilled Trades' },
  { value: 'technician', label: 'Technician', group: 'Skilled Trades' },
  { value: 'other_skilled_worker', label: 'Other Skilled Worker', group: 'Skilled Trades' },

  // Business & Commerce
  { value: 'business_owner', label: 'Business Owner', group: 'Business & Commerce' },
  { value: 'shopkeeper', label: 'Shopkeeper', group: 'Business & Commerce' },
  { value: 'trader', label: 'Trader', group: 'Business & Commerce' },
  { value: 'manufacturer', label: 'Manufacturer', group: 'Business & Commerce' },
  { value: 'real_estate', label: 'Real Estate Professional', group: 'Business & Commerce' },
  { value: 'self_employed', label: 'Self-employed', group: 'Business & Commerce' },
  { value: 'freelancer', label: 'Freelancer', group: 'Business & Commerce' },
  { value: 'other_business', label: 'Other Business Professional', group: 'Business & Commerce' },

  // Services
  { value: 'driver', label: 'Driver', group: 'Services' },
  { value: 'cook_caterer', label: 'Cook / Caterer', group: 'Services' },
  { value: 'security_personnel', label: 'Security Personnel', group: 'Services' },
  { value: 'cleaner', label: 'Cleaner', group: 'Services' },
  { value: 'delivery_transport', label: 'Delivery / Transport', group: 'Services' },
  { value: 'event_hospitality', label: 'Event / Hospitality', group: 'Services' },
  { value: 'other_service', label: 'Other Service Worker', group: 'Services' },

  // Agriculture
  { value: 'farmer', label: 'Farmer', group: 'Agriculture' },
  { value: 'dairy_livestock', label: 'Dairy / Livestock', group: 'Agriculture' },
  { value: 'agricultural_worker', label: 'Agricultural Worker', group: 'Agriculture' },
  { value: 'other_agriculture', label: 'Other Agriculture', group: 'Agriculture' },

  // Public & Community
  { value: 'government_employee', label: 'Government Employee', group: 'Public & Community' },
  { value: 'police_defence', label: 'Police / Defence', group: 'Public & Community' },
  { value: 'social_worker', label: 'Social Worker', group: 'Public & Community' },
  { value: 'ngo_community', label: 'NGO / Community Worker', group: 'Public & Community' },
  { value: 'journalist_media', label: 'Journalist / Media', group: 'Public & Community' },
  { value: 'other_public', label: 'Other Public Service', group: 'Public & Community' },

  // Other
  { value: 'homemaker', label: 'Homemaker', group: 'Other' },
  { value: 'retired', label: 'Retired', group: 'Other' },
  { value: 'laborer', label: 'Laborer', group: 'Other' },
  { value: 'job_seeker', label: 'Job Seeker', group: 'Other' },
  { value: 'unemployed', label: 'Unemployed', group: 'Other' },
  { value: 'other', label: 'Other', group: 'Other' },
]

/* Multi-select contribution list — VERBATIM, flat. */
export const CONTRIBUTION_OPTIONS: ContributionOption[] = [
  { value: 'teaching_education', label: 'Teaching / Education' },
  { value: 'quran_islamic_studies', label: 'Quran & Islamic Studies' },
  { value: 'healthcare_support', label: 'Healthcare Support' },
  { value: 'youth_activities', label: 'Youth Activities' },
  { value: 'childrens_activities', label: "Children's Activities" },
  { value: 'elderly_support', label: 'Elderly Support' },
  { value: 'masjid_maintenance', label: 'Masjid Maintenance' },
  { value: 'electrical_plumbing_repairs', label: 'Electrical / Plumbing / Repairs' },
  { value: 'cleaning_upkeep', label: 'Cleaning & Upkeep' },
  { value: 'security_safety', label: 'Security / Safety' },
  { value: 'events_programs', label: 'Events & Programs' },
  { value: 'food_hospitality', label: 'Food & Hospitality' },
  { value: 'finance_accounts', label: 'Finance / Accounts' },
  { value: 'legal_support', label: 'Legal Support' },
  { value: 'technology_it', label: 'Technology / IT' },
  { value: 'media_photography', label: 'Media / Photography' },
  { value: 'fundraising', label: 'Fundraising' },
  { value: 'community_outreach', label: 'Community Outreach' },
  { value: 'administration', label: 'Administration' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'general_volunteering', label: 'General Volunteering' },
  { value: 'other', label: 'Other' },
]

const PROFESSION_VALUES = new Set(PROFESSION_OPTIONS.map((o) => o.value))
const CONTRIBUTION_VALUES = new Set(CONTRIBUTION_OPTIONS.map((o) => o.value))

export function isValidProfession(v: unknown): boolean {
  return typeof v === 'string' && PROFESSION_VALUES.has(v)
}
export function isValidContribution(v: unknown): boolean {
  return typeof v === 'string' && CONTRIBUTION_VALUES.has(v)
}

/* The LIVE talent_profiles.category enum (CHECK-constrained, NOT NULL) — from
   src/app/api/connect/profile/route.ts CATEGORIES and the schema.ts comment. This is the ONLY set
   the DB will accept. NOTE: it is NOT the 6-value set (imam_scholar/teacher/it_digital/general) the
   profession→category derivation was specced against — see the blocker note below. */
export type CategoryEnum = 'medical' | 'legal' | 'trades' | 'education' | 'finance' | 'it' | 'other'

/* Geographic REACH ladder (talent_profiles.scope_level). Honest labels — 'nearby' is a lat/lon
   radius (no named-colony data), never an exact colony match. */
export const SCOPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'masjid', label: 'Only my masjid' },
  { value: 'nearby', label: 'Nearby masajid (~3km)' },
  { value: 'city', label: 'My city' },
  { value: 'district', label: 'My district' },
  { value: 'india', label: 'All India' },
]
const SCOPE_VALUES = new Set(SCOPE_OPTIONS.map((o) => o.value))
export function isValidScopeLevel(v: unknown): boolean {
  return typeof v === 'string' && SCOPE_VALUES.has(v)
}
// Scopes that require the profile's anchor masjid (masjid_id): 'masjid' and 'nearby' by id/coords,
// 'city'/'district' by the anchor's city/district. 'india' needs no anchor.
export const ANCHOR_DEPENDENT_SCOPES = new Set(['masjid', 'nearby', 'city', 'district'])

/* profession → category auto-derivation. Keeps the existing NOT NULL `category` column populated
 * from the new user-facing `profession` field, so every existing category reader keeps working.
 *
 * ⚠ IMPLEMENTATION NOTE (deviation from the spec, done for correctness): the approved mapping named
 * target values imam_scholar / teacher / it_digital / general — none of which exist in the LIVE
 * category enum (medical|legal|trades|education|finance|it|other, CHECK-constrained, NOT NULL).
 * Writing them would violate the CHECK and break every save, and would break existing readers (which
 * use the live enum). This maps into the LIVE enum, preserving the spec's intent: Healthcare→medical,
 * Islamic/education→education, IT/creative→it, trades→trades, and — using live values the 6-value set
 * lacked — lawyer→legal, accountants→finance; everything else → 'other' (the live catch-all; the
 * spec's default 'general' does not exist). See the session report. */
const PROFESSION_CATEGORY: Record<string, CategoryEnum> = {
  // Healthcare → medical
  doctor: 'medical', dentist: 'medical', nurse: 'medical', pharmacist: 'medical',
  physiotherapist: 'medical', hakeem_unani: 'medical', other_healthcare: 'medical',
  // Education & Islamic → education (live enum has no imam_scholar/teacher split; both fold here)
  teacher: 'education', professor_lecturer: 'education', student: 'education',
  islamic_scholar_aalim: 'education', aalimah: 'education', hafiz_hafiza: 'education',
  qari_qariah: 'education', quran_arabic_teacher: 'education', madrasa_teacher: 'education',
  // IT / creative → it
  it_software: 'it', designer_creative: 'it',
  // Legal / finance → the live enum's own values (better fidelity than 'other')
  lawyer: 'legal', chartered_accountant: 'finance', accountant_finance: 'finance',
  // Skilled trades → trades
  electrician: 'trades', plumber: 'trades', carpenter: 'trades', mechanic: 'trades',
  ac_refrigeration: 'trades', painter: 'trades', mason: 'trades', welder_fabricator: 'trades',
  tailor: 'trades', barber: 'trades', technician: 'trades', other_skilled_worker: 'trades',
  // Everything else (engineer, architect, hr, management, marketing, all business/services/
  // agriculture/public, homemaker, retired, laborer, job_seeker, unemployed, other) → 'other'.
}

export function professionToCategory(profession: string | null | undefined): CategoryEnum {
  return PROFESSION_CATEGORY[String(profession || '').trim()] || 'other'
}
